import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { routePaths } from "App/modules/urls";
import { useAuth } from "App/components/Auth";
import { Navigation } from "App/components/Navigation";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { DownloadAppButton } from "App/common/DownloadAppButton";
import { useAppRequiredDialog } from "App/routes/Main/Space/useAppRequiredDialog";
import { useCreateSpaceDialog } from "App/components/Dialog/CreateSpace/useCreateSpaceDialog";
import { useTrackEditor } from "App/components/useTrackEditor";
import {
  GlobalAudioPlayer,
  GlobalAudioTrackPreview,
} from "App/components/GlobalAudioPlayer/GlobalAudioPlayer";
import { useGlobalAudioPlayer } from "App/components/GlobalAudioPlayer";
import type {
  DiscoverPageItem,
  DiscoverPageSection,
} from "@highnote/server/src/functions/handlers/discover/types";
import type { DiscoverPageItemDetailEntry } from "./useDiscoverPageItems";
import { ReactComponent as LogoMiniSVG } from "App/common/icons-v2/logo-mini.svg";
import { ReactComponent as ExternalLinkSVG } from "App/common/icons-v2/external-link.svg";
import { ReactComponent as AddSVG } from "App/common/icons-v2/add.svg";
import { MiniSpaceTile } from "./tiles/MiniSpaceTile";
import { HeroProfileTile } from "./tiles/HeroProfileTile";
import { HeroSpaceTile } from "./tiles/HeroSpaceTile";
import { SquareSpaceTile } from "./tiles/SquareSpaceTile";

import { WelcomeBanner } from "./WelcomeBanner";
import { useDiscoverPageItems } from "./useDiscoverPageItems";
import { useMainWidth } from "App/components/Layout/useMainWidth";
import { EntitySearch } from "App/components/EntitySearch";
import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import "./Discover.scss";

const DiscoverPagePlayer = () => {
  const history = useHistory();
  const { openTrackEditor } = useTrackEditor();
  const { nowPlaying } = useGlobalAudioPlayer();
  const track = nowPlaying.track;

  return (
    <GlobalAudioPlayer
      preview={
        <GlobalAudioTrackPreview
          track={track}
          onClick={() => {
            if (!track) return;
            if (track.spaceId) {
              history.push(`/space/${track.spaceId}/${track.id}`);
            } else {
              openTrackEditor({ track });
            }
          }}
        />
      }
    />
  );
};

const DiscoverPageSectionHeader = ({
  section,
}: {
  section: DiscoverPageSection<DiscoverPageItem>;
}) => (
  <div className="discover-section-header">
    <h2 className="discover-section-title">{section.name}</h2>
    {section.description && (
      <p className="discover-section-description">{section.description}</p>
    )}
  </div>
);

const DiscoverPageSectionView = ({
  section,
  itemDetails,
}: {
  section: DiscoverPageSection<DiscoverPageItem>;
  itemDetails: Record<string, DiscoverPageItemDetailEntry>;
}) => {
  if (section.name === "Pinned") {
    return (
      <section className="discover-section">
        <DiscoverPageSectionHeader section={section} />
        <div className="discover-scroll-container">
          <div className="discover-pinned-row">
            {section.items.map((item) => (
              <MiniSpaceTile
                key={item.recordId}
                itemDetail={itemDetails[item.recordId]?.data ?? null}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (section.name === "Spotlight") {
    return (
      <section className="discover-section">
        <DiscoverPageSectionHeader section={section} />
        <div className="discover-scroll-container">
          <div className="discover-spotlight-row">
            {section.items.map((item) =>
              item.entityType === "User" ? (
                <HeroProfileTile
                  key={item.recordId}
                  itemDetail={itemDetails[item.recordId]?.data ?? null}
                />
              ) : (
                <HeroSpaceTile
                  key={item.recordId}
                  itemDetail={itemDetails[item.recordId]?.data ?? null}
                />
              ),
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="discover-section">
      <DiscoverPageSectionHeader section={section} />
      <div className="discover-scroll-container">
        <div className="discover-explore-grid">
          {section.items.map((item) => (
            <SquareSpaceTile
              key={item.recordId}
              itemDetail={itemDetails[item.recordId]?.data ?? null}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const DiscoverPage = () => {
  const { user } = useAuth();
  const history = useHistory();
  const { items, loading, loadItems, itemDetails } = useDiscoverPageItems();
  const { openCreateSpaceDialog, renderCreateSpaceDialog } =
    useCreateSpaceDialog();
  const { classes: mainWidthClasses } = useMainWidth();

  // Below M, the top-right actions can't do the desktop thing (open the library
  // or the create-space dialog) — they prompt the user to grab the app instead.
  const { isMobile, openAppRequiredDialog, appRequiredDialogComponent } =
    useAppRequiredDialog();

  // Explicitly kick off the discover items fetch when the page mounts. The
  // provider dedupes — subsequent visits to /home will reuse the cached
  // items without re-fetching. The provider also cascades into per-item
  // detail fetches when items land, so tiles hydrate without any extra
  // scheduling here.
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`highnote-discover ${mainWidthClasses}`}>
      {renderCreateSpaceDialog}

      {appRequiredDialogComponent}

      <section className="header">
        <Navigation showIndent hideBackButton>
          <h1>{user ? "Home" : <LogoMiniSVG className="discover-logo" />}</h1>
          <EntitySearch />
          <div className="discover-right-controls">
            {user ? (
              <>
                <Button
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.SECONDARY}
                  endIcon={<ExternalLinkSVG />}
                  onClick={() => {
                    if (isMobile) {
                      openAppRequiredDialog();
                      return;
                    }
                    history.push(routePaths.library);
                  }}
                >
                  Open Library
                </Button>
                <Button
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.CTA}
                  endIcon={<AddSVG />}
                  onClick={() => {
                    if (isMobile) {
                      openAppRequiredDialog();
                      return;
                    }
                    openCreateSpaceDialog({
                      parentSpace: null,
                      onConfirm: (space) => {
                        history.push(`/space/${space.id}`);
                      },
                    });
                  }}
                >
                  Create Space
                </Button>
              </>
            ) : (
              <>
                {isMobile && (
                  <DownloadAppButton
                    size={BUTTON_SIZE.SMALL}
                    theme={BUTTON_THEME.SECONDARY}
                  />
                )}
                <Button
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.CTA}
                  onClick={() => history.push(routePaths.login)}
                  endIcon={<AddSVG />}
                >
                  Create Space
                </Button>
              </>
            )}
          </div>
        </Navigation>
      </section>

      <div className="discover-content">
        <section className="discover-section">
          <WelcomeBanner />
        </section>
        {items?.sections.map((section) => (
          <DiscoverPageSectionView
            key={section.name}
            section={section}
            itemDetails={itemDetails}
          />
        ))}
      </div>

      <DiscoverPagePlayer />
    </div>
  );
};
